import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Coins, Medal, TrendUp, Trophy, Skull, Fire, Users, ArrowRight, Crown, FirstAid, Tent } from "@phosphor-icons/react";
import api, { apiErr } from "../api";
import GoldCounter from "../components/GoldCounter";
import Disclaimer from "../components/Disclaimer";
import { toast } from "sonner";

export default function Dashboard() {
  React.useEffect(() => { import("../api").then((m) => m.track("dashboard_viewed")); }, []);
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/game/dashboard").then((r) => {
      setData(r.data);
      const back = r.data?.crew_recovered_while_away || [];
      if (back.length === 1) toast.success(`${back[0]} recovered while you were away and is ready for another expedition.`);
      else if (back.length > 1) toast.success(`${back.join(", ")} recovered while you were away and are ready again.`);
    }).catch((e) => toast.error(apiErr(e)));
  }, []);

  if (!data) return <div className="py-20 text-center text-ff-muted">Loading your camp...</div>;

  const p = data.profile;

  const stats = [
    { icon: Medal, label: "Reputation", value: `${p.reputation}/100`, testid: "stat-reputation" },
    { icon: Trophy, label: "Successful", value: p.successful, testid: "stat-successful", color: "text-ff-success" },
    { icon: Skull, label: "Failed", value: p.failed, testid: "stat-failed", color: "text-ff-danger" },
    { icon: Fire, label: "Win Streak", value: p.win_streak, testid: "stat-streak" },
  ];

  return (
    <div className="space-y-6" data-testid="player-dashboard">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="font-mono-ff text-xs uppercase tracking-[0.25em] text-ff-brass">Explorer</p>
          <h1 className="font-display text-4xl font-bold tracking-tight text-ff-parchment sm:text-5xl" data-testid="dashboard-username">
            {p.username}
          </h1>
          <div className="mt-1 inline-flex items-center gap-2 rounded-sm border border-ff-brass/30 px-2 py-0.5 text-xs text-ff-muted" data-testid="dashboard-level">
            <TrendUp size={14} className="text-ff-gold" /> Level {p.level}
          </div>
        </div>
        <button
          data-testid="start-expedition-btn"
          onClick={() => nav("/play")}
          className="group flex items-center justify-center gap-2 rounded-sm bg-ff-gold px-6 py-3.5 font-semibold text-ff-bg transition-all duration-200 hover:bg-ff-brass hover:ff-glow-gold active:scale-95"
        >
          Start Expedition
          <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
        </button>
      </motion.div>

      {/* gold + stats bento */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          className="ff-panel relative col-span-2 overflow-hidden rounded-md p-6 ff-grain lg:col-span-2"
        >
          <div className="flex items-center gap-2 text-ff-brass">
            <Coins size={20} weight="duotone" />
            <span className="font-mono-ff text-xs uppercase tracking-widest">Virtual Gold</span>
          </div>
          <div className="mt-3 text-glow-gold" data-testid="gold-balance">
            <GoldCounter value={data.balance} className="text-4xl font-bold text-ff-gold sm:text-5xl" />
          </div>
        </motion.div>

        {stats.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * i }}
            className="ff-panel rounded-md p-5"
            data-testid={s.testid}
          >
            <s.icon size={22} weight="duotone" className={`mb-2 ${s.color || "text-ff-gold"}`} />
            <div className="font-mono-ff text-2xl font-bold text-ff-parchment">{s.value}</div>
            <div className="text-xs text-ff-muted">{s.label}</div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* rank */}
        <div className="ff-panel rounded-md p-6" data-testid="rank-card">
          <div className="mb-3 flex items-center gap-2">
            <Crown size={20} weight="duotone" className="text-ff-gold" />
            <h2 className="font-display text-2xl font-semibold">Expedition Rank</h2>
          </div>
          <div className="text-glow-gold font-display text-3xl font-bold text-ff-gold" data-testid="rank-name">{data.rank.current.name}</div>
          {data.rank.next ? (
            <>
              <div className="mt-3 mb-1 flex justify-between text-xs text-ff-muted">
                <span>Toward {data.rank.next.name}</span>
                <span className="font-mono-ff">{Math.round(data.rank.progress * 100)}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-ff-bg">
                <div className="h-full bg-ff-gold" style={{ width: `${data.rank.progress * 100}%` }} />
              </div>
              {data.rank.missing.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-xs text-ff-muted">
                  {data.rank.missing.map((m, i) => <li key={i}>• {m}</li>)}
                </ul>
              )}
            </>
          ) : (
            <p className="mt-2 text-sm text-ff-brass">Highest rank achieved — a true Fortune Legend.</p>
          )}
        </div>

        {/* medical supplies */}
        <div className="ff-panel rounded-md p-6" data-testid="medical-card">
          <div className="mb-3 flex items-center gap-2">
            <FirstAid size={20} weight="duotone" className="text-ff-danger" />
            <h2 className="font-display text-2xl font-semibold">Medical Supplies</h2>
          </div>
          <div className="font-mono-ff text-4xl font-bold text-ff-gold">{data.medical_supplies}</div>
          <p className="mt-1 text-xs text-ff-muted">Earned through expeditions. Spend them in the Recovery Camp to heal injured crew.</p>
        </div>

        {/* recovery camp */}
        <div className="ff-panel rounded-md p-6" data-testid="camp-card">
          <div className="mb-3 flex items-center gap-2">
            <Tent size={20} weight="duotone" className="text-ff-gold" />
            <h2 className="font-display text-2xl font-semibold">Recovery Camp</h2>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <CampStat label="Injured" value={data.camp_summary.injured} danger />
            <CampStat label="Recovering" value={data.camp_summary.recovering} />
            <CampStat label="Stressed" value={data.camp_summary.stressed} />
          </div>
          <button onClick={() => nav("/camp")} data-testid="open-camp-btn" className="mt-4 w-full rounded-sm border border-ff-brass/40 py-2 text-sm text-ff-parchment transition-colors hover:border-ff-gold hover:text-ff-gold">
            Open Camp →
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* crew summary */}
        <div className="ff-panel rounded-md p-6">
          <div className="mb-4 flex items-center gap-2">
            <Users size={20} weight="duotone" className="text-ff-gold" />
            <h2 className="font-display text-2xl font-semibold">Your Crew</h2>
          </div>
          <div className="grid grid-cols-2 gap-3" data-testid="crew-summary">
            {data.crew.map((c) => (
              <div key={c.id} className="flex items-center gap-3 rounded-sm border border-ff-brass/20 bg-ff-bg/40 p-2">
                <div className="relative">
                  <img src={c.portrait} alt={c.name} className="h-11 w-11 rounded-sm object-cover" loading="lazy" />
                  <span className={`absolute -right-1 -top-1 h-3 w-3 rounded-full border border-ff-bg ${c.available ? "bg-ff-success" : "bg-ff-danger"}`} title={c.available ? "Available" : c.unavailable_reason} />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-ff-parchment">{c.name}</div>
                  <div className="truncate text-xs text-ff-brass">Lv {c.level} · {c.strong_skills.join(" · ")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* recent history */}
        <div className="ff-panel rounded-md p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-2xl font-semibold">Recent Expeditions</h2>
            <button onClick={() => nav("/history")} data-testid="view-all-history" className="text-xs text-ff-brass hover:text-ff-gold">
              View all →
            </button>
          </div>
          {data.history.length === 0 ? (
            <p className="py-8 text-center text-sm text-ff-muted">No expeditions yet. Your legend awaits.</p>
          ) : (
            <div className="space-y-2" data-testid="recent-history">
              {data.history.map((h) => (
                <button
                  key={h.id}
                  onClick={() => nav(`/history/${h.id}`)}
                  data-testid={`history-row-${h.id}`}
                  className="flex w-full items-center justify-between rounded-sm border border-ff-brass/15 bg-ff-bg/40 px-4 py-3 text-left transition-colors hover:border-ff-brass/40"
                >
                  <div>
                    <div className="text-sm font-medium text-ff-parchment">{h.expedition_name}</div>
                    <div className="text-xs text-ff-muted">{new Date(h.date).toLocaleDateString()} · {h.outcome}</div>
                  </div>
                  <div className={`font-mono-ff text-sm font-bold ${h.gold_result >= 0 ? "text-ff-success" : "text-ff-danger"}`}>
                    {h.gold_result >= 0 ? "+" : ""}{h.gold_result}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <Disclaimer />
    </div>
  );
}

function CampStat({ label, value, danger }) {
  return (
    <div className="rounded-sm bg-ff-bg/40 py-2">
      <div className={`font-mono-ff text-xl font-bold ${danger && value > 0 ? "text-ff-danger" : "text-ff-parchment"}`}>{value}</div>
      <div className="text-ff-muted">{label}</div>
    </div>
  );
}
